import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { getApiBase } from '../lib/api-client'
import { App } from './App'
import FilterBar from './FilterBar'

interface League {
  slug: string
  displayName: string
  category: string
}

interface DashboardProps {
  instanceId: string
  sports?: string[]
  updateHash?: boolean
  /** Forwarded to each sport's App — see WidgetConfig.emptyState. */
  emptyState?: 'message' | 'hidden'
  /** Forwarded to each sport's App — see WidgetConfig.subAffiliate. */
  subAffiliate?: string
}

const STORAGE_KEY = 'odds-dashboard-league'

function getInitialLeague(leagues: League[], updateHash?: boolean): string {
  // 1. Check URL hash
  if (updateHash) {
    const hash = window.location.hash.slice(1)
    if (hash && leagues.some(l => l.slug === hash)) {
      return hash
    }
  }

  // 2. Check localStorage
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && leagues.some(l => l.slug === stored)) {
    return stored
  }

  // 3. Fall back to first available league
  return leagues[0]?.slug ?? ''
}

function Dashboard({ instanceId, sports, updateHash, emptyState, subAffiliate }: DashboardProps) {
  const { data: allLeagues, isPending, error: leaguesError } = useQuery<League[]>({
    queryKey: ['leagues'],
    queryFn: async () => {
      const response = await fetch(`${getApiBase()}/api/v1/leagues`)
      if (!response.ok) {
        throw new Error(`Failed to fetch leagues: ${response.status}`)
      }
      return response.json()
    },
    staleTime: 60_000,
  })

  const leagues = allLeagues
    ? sports
      ? allLeagues.filter(l => sports.includes(l.slug))
      : allLeagues
    : []

  const [selectedLeague, setSelectedLeague] = useState<string>('')

  useEffect(() => {
    if (leagues.length > 0 && !selectedLeague) {
      setSelectedLeague(getInitialLeague(leagues, updateHash))
    }
  }, [leagues, selectedLeague, sports, updateHash])

  useEffect(() => {
    if (!updateHash) return
    const onHashChange = () => {
      const hash = window.location.hash.slice(1)
      if (hash && leagues.some(l => l.slug === hash)) {
        setSelectedLeague(hash)
      }
    }
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [updateHash, leagues])

  const handleSelect = (slug: string) => {
    setSelectedLeague(slug)
    localStorage.setItem(STORAGE_KEY, slug)
    if (updateHash) {
      window.location.hash = slug
    }
  }

  if (leaguesError) {
    return <div className="text-red-500 p-5 dark:text-red-400">Error loading leagues: {leaguesError.message}</div>
  }

  if (isPending) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="w-10 h-10 border-4 border-[#C8E2FF] border-t-[#0db5ff] rounded-full animate-spin dark:border-(--ow-hairline) dark:border-t-(--ow-accent)" />
      </div>
    )
  }

  if (leagues.length === 0) return null

  return (
    <div className="flex flex-col gap-4" style={{ width: '100%' }}>
      <FilterBar
        leagues={leagues}
        selected={selectedLeague}
        onSelect={handleSelect}
      />
      {selectedLeague
        ? (
            <div style={{ width: '100%' }}>
              <App
                key={selectedLeague}
                instanceId={instanceId}
                league={selectedLeague}
                emptyState={emptyState}
                subAffiliate={subAffiliate}
              />
            </div>
          )
        : <div className="text-gray-400 p-5 dark:text-(--ow-meta)">No league selected</div>}
    </div>
  )
}

const queryClient = new QueryClient()

export default function DashboardWidget(props: DashboardProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <Dashboard {...props} />
    </QueryClientProvider>
  )
}
